import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import client, { extractError } from "@/api";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await client.post("/auth/forgot-password", { email: email.trim() });
      setMessage(res.data?.message
        || "If an account exists for this email address, a password reset link has been sent.");
    } catch (err) {
      setError(extractError(err, "Something went wrong. Please try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <Seo
        title="Forgot Password · Barter India"
        description="Request a secure password reset link for your Barter India account."
        path="/forgot-password"
        robots="noindex,follow"
      />
      <section className="auth-intro">
        <div className="brand-lockup" data-testid="brand-lockup"><span className="brand-mark">B</span><span>Barter India</span></div>
        <div className="intro-copy">
          <p className="eyebrow">ACCOUNT RECOVERY</p>
          <h1>Reset your<br /><em>password</em><br />securely.</h1>
          <p>Enter your registered work email and we'll send you a secure link to choose a new password.</p>
        </div>
        <div className="intro-footer"><span>Barter India Support</span><span className="status-dot">● Encrypted delivery</span></div>
      </section>
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <h2>Forgot your password?</h2>
          <p className="muted">We'll email you a secure reset link that expires in 60 minutes.</p>
          <form onSubmit={submit} data-testid="forgot-password-form">
            <label htmlFor="email">Registered work email</label>
            <input id="email" data-testid="forgot-email-input" type="email"
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@company.com" required autoComplete="email" />
            {error && <div className="form-error" data-testid="forgot-error">{error}</div>}
            {message && <div className="form-note" data-testid="forgot-success">{message}</div>}
            <button className="primary-button" type="submit"
                    data-testid="forgot-submit-button" disabled={busy}>
              {busy ? "Sending…" : "Send Reset Link"} <ArrowUpRight size={17} />
            </button>
          </form>
          <p className="form-note">
            Remembered it? <Link to="/login" data-testid="forgot-back-to-login">Back to sign in</Link>
          </p>
          <p className="form-note" data-testid="security-note">
            <ShieldCheck size={15} /> We never share your email or account status
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
